import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type SubmitEvent, useState } from 'react'

import type { BranchSettings } from '@/bindings/BranchSettings'
import type { OrderingMode } from '@/bindings/OrderingMode'
import type { SetShopOpenRequest } from '@/bindings/SetShopOpenRequest'
import type { UpdateBranchSettingsRequest } from '@/bindings/UpdateBranchSettingsRequest'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { Checkbox, Field, Input, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { backOfficeKeys, branchSettingsPath, branchSettingsQuery } from '@/features/back-office/api'
import { claim, request } from '@/lib/api'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { canManageBranch } from '@/lib/roles'


/** Mirror `romi_domain::MAX_*` (G4): the inputs' native bounds; the backend is the authority. */
export const MAX_OPEN_ORDER_EXPIRY_MINUTES = 1440
export const MAX_ACTIVE_ORDER_CAP = 100
export const MAX_ORDER_PIN_LEN = 20

const MODES: readonly OrderingMode[] = ['open', 'login', 'closed']

interface Scope {
  restaurantId: string
  branchId: string
}

/**
 * Branch settings page (PRD, G4): the daily open/close-shop switch and the standing ordering
 * policy with its per-mode anti-spam levers. Admin and above in the branch.
 */
export function BranchSettingsPage() {
  const t = useT()
  const { restaurantId, branchId, context } = useManagedBranch()

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('branchSettings.title')}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
    </div>
  )
  if (!canManageBranch(context.role)) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p role="alert" className="text-muted text-sm">
          {t('branchSettings.adminOnly')}
        </p>
      </Page>
    )
  }

  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      <Settings scope={{ restaurantId, branchId }} />
    </Page>
  )
}

function Settings({ scope }: { scope: Scope }) {
  const t = useT()
  const settings = useQuery(branchSettingsQuery(scope.restaurantId, scope.branchId))
  if (settings.isPending) return <p className="text-muted text-sm">{t('common.loading')}</p>
  if (settings.error) {
    return (
      <div className="flex flex-col gap-2">
        <FormError error={settings.error} />
        <Button variant="secondary" onClick={() => void settings.refetch()} className="self-start">
          {t('common.retry')}
        </Button>
      </div>
    )
  }
  return (
    <>
      <ShopCard scope={scope} settings={settings.data} />
      <PolicyForm key={settings.data.id} scope={scope} settings={settings.data} />
    </>
  )
}

/** Both writes answer with the whole row; the page reads it from this one cache entry. */
function useSettingsPatch({ restaurantId, branchId }: Scope) {
  const queryClient = useQueryClient()
  return (next: BranchSettings) => queryClient.setQueryData(backOfficeKeys.branchSettings(restaurantId, branchId), next)
}

/** The PRD's two buttons, each behind a second press so a mis-tap on the live one changes nothing either. */
function ShopCard({ scope, settings }: { scope: Scope; settings: BranchSettings }) {
  const t = useT()
  const patch = useSettingsPatch(scope)
  const [status, setStatus] = useState<'opened' | 'closed' | undefined>()
  const flip = useMutation({
    mutationFn: (is_open: boolean) => {
      const body: SetShopOpenRequest = { is_open }
      return request<BranchSettings>(branchSettingsPath(scope.restaurantId, scope.branchId, '/open'), { method: 'PUT', body })
    },
    onSuccess: (next) => {
      patch(next)
      setStatus(next.is_open ? 'opened' : 'closed')
    },
  })

  return (
    <Card className="flex flex-col gap-4" aria-label={t('branchSettings.shopTitle')}>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">{t('branchSettings.shopTitle')}</h2>
        <Badge tone={settings.is_open ? 'success' : 'muted'}>
          {t(settings.is_open ? 'branchSettings.status.open' : 'branchSettings.status.closed')}
        </Badge>
      </div>
      <p className="text-muted text-sm">{t('branchSettings.shopHint')}</p>
      <div className="flex flex-wrap gap-3">
        <ConfirmButton
          label={t('branchSettings.openShop')}
          variant="primary"
          size="md"
          disabled={settings.is_open}
          confirmLabel={t('branchSettings.openConfirm')}
          confirmVariant="primary"
          confirm={() => flip.mutateAsync(true)}
        />
        <ConfirmButton
          label={t('branchSettings.closeShop')}
          variant="secondary"
          size="md"
          disabled={!settings.is_open}
          confirmLabel={t('branchSettings.closeConfirm')}
          confirm={() => flip.mutateAsync(false)}
        />
      </div>
      <FormError error={flip.error} />
      {status && !flip.isPending && (
        <p role="status" className="text-muted text-sm">
          {t(status === 'opened' ? 'branchSettings.shopOpened' : 'branchSettings.shopClosed')}
        </p>
      )}
    </Card>
  )
}

interface Draft {
  mode: OrderingMode
  /** Kept as typed so a half-edited number does not snap back. */
  expiryMinutes: string
  activeOrderCap: string
  pinGate: boolean
  pin: string
  deliveryAddress: boolean
}

function draftFrom(settings: BranchSettings): Draft {
  return {
    mode: settings.ordering_mode,
    expiryMinutes: String(settings.open_order_expiry_minutes),
    activeOrderCap: String(settings.active_order_cap),
    pinGate: settings.order_pin !== null,
    pin: settings.order_pin ?? '',
    deliveryAddress: settings.delivery_address_enabled,
  }
}

/**
 * Every lever travels every time; a hidden one sends its saved value, since the backend validates
 * it regardless of mode and a refusal would land on a field nobody can see. The PIN only while the
 * gate is on.
 */
function toRequest(draft: Draft, saved: BranchSettings): UpdateBranchSettingsRequest {
  return {
    ordering_mode: draft.mode,
    open_order_expiry_minutes: draft.mode === 'open' ? Number(draft.expiryMinutes) : saved.open_order_expiry_minutes,
    active_order_cap: draft.mode === 'login' ? Number(draft.activeOrderCap) : saved.active_order_cap,
    order_pin: draft.pinGate ? draft.pin.trim() : null,
    delivery_address_enabled: draft.deliveryAddress,
  }
}

function PolicyForm({ scope, settings }: { scope: Scope; settings: BranchSettings }) {
  const t = useT()
  const patch = useSettingsPatch(scope)
  const [draft, setDraft] = useState(() => draftFrom(settings))
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      request<BranchSettings>(branchSettingsPath(scope.restaurantId, scope.branchId), { method: 'PUT', body: toRequest(draft, settings) }),
    onSuccess: (next) => {
      patch(next)
      setDraft(draftFrom(next))
      setSaved(true)
    },
  })

  const edit = (changes: Partial<Draft>) => {
    if (save.error) save.reset()
    setSaved(false)
    setDraft((current) => ({ ...current, ...changes }))
  }

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  // A verdict lands on its field; the request only carries a value for a lever that is shown.
  const expiryError = claim(save.error, 'INVALID_EXPIRY_MINUTES')
  const capError = claim(save.error, 'INVALID_ACTIVE_ORDER_CAP')
  const pinError = claim(save.error, 'INVALID_ORDER_PIN')
  const fieldError = expiryError ?? capError ?? pinError

  return (
    <Card className="flex flex-col gap-4" aria-label={t('branchSettings.orderingTitle')}>
      <h2 className="text-lg font-semibold">{t('branchSettings.orderingTitle')}</h2>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('branchSettings.mode')} hint={t(`branchSettings.modeHint.${draft.mode}`)}>
          <Select value={draft.mode} onChange={(e) => edit({ mode: e.target.value as OrderingMode })}>
            {MODES.map((mode) => (
              <option key={mode} value={mode}>
                {t(`branchSettings.mode.${mode}`)}
              </option>
            ))}
          </Select>
        </Field>
        {draft.mode === 'open' && (
          <Field
            label={t('branchSettings.expiry')}
            hint={t('branchSettings.expiryHint', { max: MAX_OPEN_ORDER_EXPIRY_MINUTES })}
            error={expiryError}
          >
            <Input
              type="number"
              inputMode="numeric"
              required
              min={1}
              max={MAX_OPEN_ORDER_EXPIRY_MINUTES}
              value={draft.expiryMinutes}
              onChange={(e) => edit({ expiryMinutes: e.target.value })}
              className="w-32"
            />
          </Field>
        )}
        {draft.mode === 'login' && (
          <Field label={t('branchSettings.cap')} hint={t('branchSettings.capHint', { max: MAX_ACTIVE_ORDER_CAP })} error={capError}>
            <Input
              type="number"
              inputMode="numeric"
              required
              min={1}
              max={MAX_ACTIVE_ORDER_CAP}
              value={draft.activeOrderCap}
              onChange={(e) => edit({ activeOrderCap: e.target.value })}
              className="w-32"
            />
          </Field>
        )}
        <div className="flex flex-col gap-1">
          <Checkbox label={t('branchSettings.pinGate')} checked={draft.pinGate} onChange={(e) => edit({ pinGate: e.target.checked })} />
          <p className="text-muted text-sm">{t('branchSettings.pinGateHint')}</p>
        </div>
        {draft.pinGate && (
          <Field label={t('branchSettings.pin')} hint={t('branchSettings.pinHint', { max: MAX_ORDER_PIN_LEN })} error={pinError}>
            <Input
              required
              maxLength={MAX_ORDER_PIN_LEN}
              autoComplete="off"
              value={draft.pin}
              onChange={(e) => edit({ pin: e.target.value })}
              className="w-48"
            />
          </Field>
        )}
        <div className="flex flex-col gap-1">
          <Checkbox
            label={t('branchSettings.deliveryAddress')}
            checked={draft.deliveryAddress}
            onChange={(e) => edit({ deliveryAddress: e.target.checked })}
          />
          <p className="text-muted text-sm">{t('branchSettings.deliveryAddressHint')}</p>
        </div>
        <FormError error={fieldError ? undefined : save.error} />
        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending}>
            {t('common.save')}
          </Button>
          {saved && (
            <p role="status" className="text-muted text-sm">
              {t('branchSettings.saved')}
            </p>
          )}
        </div>
      </form>
    </Card>
  )
}
